class GrabPredictiveSearch extends HTMLElement {

    constructor() {
        super();
        this.input = this.querySelector('input[type="search"]');
        this.resultsWrapper = this.querySelector('.grab-search-results-wrapper');
        this.categoryWrapper = this.querySelector('.grab-search-category-wrapper');
        this.loadingWrapper = this.querySelector('.grab-search-loading');
        this.iconSearch = this.querySelector('.icon-search');
        this.availableCheckbox = this.querySelector('.grab-search-available-check input[type="checkbox"]');
        this.resultsLink = this.querySelector('.grab-search-results-link');
        this.resultsLinkCount = this.querySelector('.grab-search-results-link-count');
        this.perPage = Number(this.dataset.perPage || 8);

        this.backupFilters()
        this.loadFilters()

        this.availableCheckbox.addEventListener('change', (event) => {
            this.requestSearch()
        });

        this.input.addEventListener(
            'input',
            this.debounce((event) => {
                this.onChange(event);
            }, 300).bind(this)
        );

        // refs #25905: カテゴリ、ブランドの選択ボタンの a.href を無効にして、クリックイベントを取得する
        this.querySelectorAll('a.grab-filter-item').forEach((filterItem) => {
            filterItem.href = 'javascript:void(0)';
            filterItem.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const tag = filterItem.dataset.filterTag;
                const type = filterItem.dataset.filterType;
                const href = this.makeResultsLink({ tag, type });
                window.location.href = href;
            });
        });

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        this.input.focus();
                        if (this.input.value.trim().length) {
                            this.onChange();
                        }
                    }
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(this);
    }

    cancel() {
        this.restoreFilters()
    }

    /** refs #25908: フィルタ関連の状態をバックアップしておく */
    backupFilters() {
        this.filterTagsBackup = Array.from(this.querySelectorAll('.grab-filter-tags .grab-filter-tag-item')).map(node => node.cloneNode(true));
        this.searchTermBackup = this.input.value.trim();
        this.availabilityBackup = this.availableCheckbox.checked;
    }

    /** refs #25908: バックアップしたフィルタを復元する */
    restoreFilters() {
        const filterTags = this.querySelector('.grab-filter-tags')
        Array.from(filterTags.querySelectorAll('.grab-filter-tag-item')).forEach(tagItem => tagItem.remove())
        this.filterTagsBackup.forEach(tagItem => filterTags.appendChild(tagItem.cloneNode(true)))

        this.input.value = this.searchTermBackup;
        this.availableCheckbox.checked = this.availabilityBackup;
        this.loadFilters();
    }

    /** 今設定されているフィルタをロードする */
    loadFilters() {
        this.searchTerm = this.input.value.trim();
        this.tagSets = []

        const filterTags = this.querySelector('.grab-filter-tags');
        if (filterTags) {
            filterTags.querySelectorAll('.grab-filter-tag-item').forEach((tagItem, i) => {
                const tag = tagItem.dataset.filterTag;
                const type = tagItem.dataset.filterType;
                this.tagSets.push({ tag, type });

                const icon = tagItem.querySelector('.icon-close');
                icon.addEventListener('click', (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    tagItem.remove();
                    this.tagSets.splice(i, 1);
                    if (this.input.value.trim().length > 0) {
                        this.requestSearch();
                    }
                }); 
            });
        }
    }

    onChange() {
        this.searchTerm = this.input.value.trim();
        if (!this.searchTerm.length) {
            this.closeResults();
            return;
        }
        this.iconSearch.style.display = 'none';
        this.requestSearch();
    }

    showLoading() {
        this.loadingWrapper.classList.add('visible');
        this.resultsWrapper.style.display = 'none';
        this.categoryWrapper.style.display = 'none';
    }

    /** GraphQL に渡すクエリを作成する */
    makeQueries(buttonTagSet) {

        const flatTags = [];
        const appendTags = (tagSet) => {
            tagSet.tag.split('+').forEach((part) => {
                if (part) {
                    flatTags.push(part);
                }
            });
        }

        if (buttonTagSet) {
            appendTags(buttonTagSet);
        }

        this.tagSets.forEach((tagSet) => {
            if (buttonTagSet && buttonTagSet.type === tagSet.type) return
            appendTags(tagSet);
        });

        // 「refs #25900: 検索ドロワーでカテゴリを選択すると、選択カテゴリが表示されない」ので、tag: を追加する
        let q = this.searchTerm
        if (!q) {
            q = flatTags.map(t => `tag:${t}`).join(' ')
        }
        return {
            q,
            tags: flatTags,
            availability: this.availableCheckbox.checked ? '1' : '',
        };
    }

    /** 「xx件の結果を見る」ボタンのリンク先 */
    makeResultsLink(buttonTagSet) {
        const sp = this.makeQueries(buttonTagSet)
        const query = [];
        query.push('q=' + encodeURIComponent(sp.q))
        sp.tags.forEach((tag) => {
            query.push('filter.p.tag=' + tag)
        });
        if (sp.availability) {
            query.push('filter.v.availability=1');
        }
        return window.Shopify.routes.root + 'search?' + query.join('&');
    }

    requestSearch() {
        const shared = window.grabSearchStorefrontShared;
        const cfg = window.grabStorefrontSearchConfig || {};
        if (!shared || !cfg.token) {
            this.closeResults();
            return;
        }
        const sp = this.makeQueries()
        
        const storefrontQuery = shared.buildStorefrontQuery(sp);
        const storefrontCountQuery = shared.buildStorefrontCountQuery(sp);
        const storefrontFilters = shared.buildStorefrontFilters(sp);
        const variables = {
            query: storefrontQuery,
            first: this.perPage,
            sortKey: 'CREATED_AT',
            reverse: true,
        };

        this.showLoading();
        Promise.all([
            shared.fetchStorefront(cfg, shared.PRODUCTS_QUERY, variables),
            shared.fetchStorefront(cfg, shared.PRODUCTS_COUNT_QUERY, {
                query: storefrontCountQuery,
                filters: storefrontFilters,
            }),
        ])
            .then(([productsData, countData]) => {
                const data = productsData && productsData.products;
                const edges = (data && data.edges) || [];
                const totalCountRaw = Number(
                    (countData && countData.search && countData.search.totalCount) || 0
                );
                const totalCount = Number.isFinite(totalCountRaw) ? totalCountRaw : 0;
                const soldOutLabel =
                    (window.variantStrings && window.variantStrings.soldOut) || '売り切れ';

                if (!edges.length) {
                    this.resultsWrapper.innerHTML = '<p class="grab-search-empty">「' + shared.escapeHtml(this.searchTerm) + '」の結果は見つかりませんでした。</p>';
                    this.updateResultsLink(totalCount, sp);
                    this.openResults();
                    return;
                }

                const cards = edges
                    .map((edge) =>
                        shared.renderProductCard(edge.node, {
                            cardClass: 'grab-search-result-item',
                            linkClass: 'grab-search-result-item__link',
                            mediaClass: 'grab-search-result-item__media',
                            textClass: 'grab-search-result-item__text',
                            titleClass: 'grab-search-result-item__title',
                            priceRowClass: 'grab-search-result-item__price-row',
                            pricePartClass: 'grab-search-result-item__price-part',
                            compareClass: 'grab-search-result-item__price-part--compare',
                            badgeClass: 'grab-search-result-item__badge',
                            soldOutLabel: soldOutLabel,
                            itemClass: '',
                        })
                    )
                    .join('');
                this.resultsWrapper.innerHTML = '<div class="grab-search-results"><div class="grab-search-result-items">' + cards + '</div></div>';
                this.updateResultsLink(totalCount);
                this.openResults();
            })
            .catch((error) => {
                console.error('GrabPredictiveSearch: storefront error', error);
                this.closeResults();
            });
    }

    updateResultsLink(totalCount) {
        if (!this.resultsLink || !this.resultsLinkCount) {
            return;
        }
        this.resultsLinkCount.textContent = String(totalCount || 0);
        this.resultsLink.href = this.makeResultsLink();
    }

    openResults() {
        this.resultsWrapper.style.display = 'block';
        this.loadingWrapper.classList.remove('visible');
        this.categoryWrapper.style.display = 'none';
        if (this.resultsLink) {
            this.resultsLink.style.display = 'flex';
        }
        this.input.setAttribute('aria-expanded', 'true');
    }

    closeResults() {
        this.loadingWrapper.classList.remove('visible');
        this.resultsWrapper.style.display = 'none';
        this.categoryWrapper.style.display = 'block';
        this.iconSearch.style.display = 'block';
        if (this.resultsLink) {
            this.resultsLink.style.display = 'none';
        }
        this.input.setAttribute('aria-expanded', 'false');
    }

    debounce(fn, wait) {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
        };
    }
}

customElements.define('grab-predictive-search', GrabPredictiveSearch);
